import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { dataService, Registration } from '../services/dataService';
import { DashboardHeader } from '../components';
import '../styles/reports-redesign.css';
import '../styles/dashboard-header.css';

// Grouped student data structure
interface GroupedStudent {
  id: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female';
  dateOfBirth: string;
  school: string;
  schoolId: string;
  events: string[];
  ageGroup: string;
  status: 'pending' | 'approved' | 'rejected';
  registrationDate: string;
}

interface ReportFilters {
  school: string;
  event: string;
  ageGroup: string;
  gender: string;
  region: string;
  dateFrom: string;
  dateTo: string;
}

const Reports: React.FC = () => {
  const { user } = useApp();
  
  // Add spinner animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [groupedStudents, setGroupedStudents] = useState<GroupedStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteredStudents, setFilteredStudents] = useState<GroupedStudent[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Filters popup state for mobile
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);
  
  // School search state
  const [schoolSearchQuery, setSchoolSearchQuery] = useState('');
  const [mobileSchoolSearchQuery, setMobileSchoolSearchQuery] = useState('');
  
  const [filters, setFilters] = useState<ReportFilters>({
    school: '',
    event: '',
    ageGroup: '',
    gender: '',
    region: '',
    dateFrom: '',
    dateTo: ''
  });

  // Function to group registrations by student
  const groupRegistrationsByStudent = (registrations: Registration[]): GroupedStudent[] => {
    const grouped = new Map<string, GroupedStudent>();

    registrations.forEach(registration => {
      // Create unique key using school_id, first_name, last_name, and date_of_birth
      const key = `${registration.school_id}-${registration.first_name}-${registration.last_name}-${registration.date_of_birth}`;
      
      if (grouped.has(key)) {
        // Add sport to existing student only if this registration is approved
        const existingStudent = grouped.get(key)!;
        if (registration.status === 'approved' && !existingStudent.events.includes(registration.sport)) {
          existingStudent.events.push(registration.sport);
        }
      } else {
        // Create new student entry only if this registration is approved
        if (registration.status === 'approved') {
          grouped.set(key, {
            id: registration.id,
            firstName: registration.first_name,
            lastName: registration.last_name,
            gender: registration.gender,
            dateOfBirth: registration.date_of_birth,
            school: '', // Will be filled when we get school data
            schoolId: registration.school_id,
            events: [registration.sport],
            ageGroup: registration.age_group,
            status: registration.status,
            registrationDate: registration.created_at?.split('T')[0] || ''
          });
        }
      }
    });

    // Filter out students with no approved events
    return Array.from(grouped.values()).filter(student => student.events.length > 0);
  };



  // Load data from database
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // For coaches and headteachers, only get registrations from their assigned school
        // For other roles (admin), get all registrations
        const registrationFilters = (user?.role === 'coach' || user?.role === 'headteacher' || user?.role === 'headmaster') && user?.schoolId 
          ? { schoolId: user.schoolId }
          : {};
        
        // Get registrations and schools with regions in parallel
        const [allRegistrations, schoolsWithRegionData] = await Promise.all([
          dataService.getRegistrations(registrationFilters),
          dataService.getSchoolsWithRegions()
        ]);
        
        setRegistrations(allRegistrations);
        setSchoolsWithRegions(schoolsWithRegionData);
        
        // Group by student
        const grouped = groupRegistrationsByStudent(allRegistrations);
        
        // Get school names for each student using the schools with regions data
        const groupedWithSchoolNames = await Promise.all(
          grouped.map(async (student) => {
            try {
              // First try to find in schoolsWithRegionData
              const schoolWithRegion = schoolsWithRegionData.find(s => s.id === student.schoolId);
              if (schoolWithRegion) {
                return {
                  ...student,
                  school: schoolWithRegion.name
                };
              }
              
              // Fallback to dataService if not found
              const schoolData = await dataService.getSchoolById(student.schoolId);
              return {
                ...student,
                school: schoolData?.name || 'Unknown School'
              };
            } catch (error) {
              console.error(`Error fetching school for ID ${student.schoolId}:`, error);
              return {
                ...student,
                school: 'Unknown School'
              };
            }
          })
        );
        
        setGroupedStudents(groupedWithSchoolNames);
        setFilteredStudents(groupedWithSchoolNames);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for regions data
  const [schoolsWithRegions, setSchoolsWithRegions] = useState<Array<{id: string, name: string, region?: string}>>([]);

  // Filter options - we'll derive these from actual data
  // For coaches and headteachers, only show their assigned school in the filter
  const schools = (user?.role === 'coach' || user?.role === 'headteacher' || user?.role === 'headmaster')
    ? Array.from(new Set(groupedStudents.map(s => s.school).filter(Boolean)))
    : ['All Schools', ...Array.from(new Set(groupedStudents.map(s => s.school).filter(Boolean)))];
  const events = ['All Events', ...Array.from(new Set(registrations.map(r => r.sport)))];
  const ageGroups = ['All Age Groups', ...Array.from(new Set(registrations.map(r => r.age_group)))];
  const genders = ['All Genders', 'male', 'female'];
  const regions = ['All Regions', ...Array.from(new Set(schoolsWithRegions.map(s => s.region).filter(Boolean)))];

  const applyFilters = () => {
    // Start with all grouped students (already filtered for approved events only)
    let filtered = groupedStudents;

    // For non-coach and non-headteacher users, apply school filter if selected and not "All Schools"
    // For coaches and headteachers, school filtering is already done at data loading level
    if (!['coach', 'headteacher', 'headmaster'].includes(user?.role || '') && filters.school && filters.school !== 'All Schools') {
      filtered = filtered.filter(student => student.school === filters.school);
    }

    if (filters.event && filters.event !== 'All Events') {
      filtered = filtered.filter(student => student.events.includes(filters.event));
    }

    if (filters.ageGroup && filters.ageGroup !== 'All Age Groups') {
      filtered = filtered.filter(student => student.ageGroup === filters.ageGroup);
    }

    if (filters.gender && filters.gender !== 'All Genders') {
      filtered = filtered.filter(student => student.gender === filters.gender);
    }

    if (filters.region && filters.region !== 'All Regions') {
      // Filter by region - find the school's region
      filtered = filtered.filter(student => {
        const schoolWithRegion = schoolsWithRegions.find(s => s.name === student.school);
        return schoolWithRegion?.region === filters.region;
      });
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(student => new Date(student.registrationDate) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      filtered = filtered.filter(student => new Date(student.registrationDate) <= new Date(filters.dateTo));
    }

    setFilteredStudents(filtered);
  };

  const handleFilterChange = (field: keyof ReportFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      school: '',
      event: '',
      ageGroup: '',
      gender: '',
      region: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, groupedStudents]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, filteredStudents]);

  const downloadCSV = () => {
    // Get all approved registrations that match filters
    const approvedRegistrations = registrations.filter(reg => {
      if (reg.status !== 'approved') return false;
      
      // Apply filters
      // For non-coach and non-headteacher users, apply school filter if selected and not "All Schools"
      // For coaches and headteachers, school filtering is already done at data loading level
      if (!['coach', 'headteacher', 'headmaster'].includes(user?.role || '') && filters.school && filters.school !== 'All Schools') {
        const matchingStudent = groupedStudents.find(s => s.schoolId === reg.school_id);
        if (!matchingStudent || matchingStudent.school !== filters.school) return false;
      }
      
      if (filters.region && filters.region !== 'All Regions') {
        const schoolWithRegion = schoolsWithRegions.find(s => s.id === reg.school_id);
        if (!schoolWithRegion || schoolWithRegion.region !== filters.region) return false;
      }
      
      if (filters.event && filters.event !== 'All Events') {
        if (reg.sport !== filters.event) return false;
      }
      
      if (filters.ageGroup && filters.ageGroup !== 'All Age Groups') {
        if (reg.age_group !== filters.ageGroup) return false;
      }
      
      if (filters.gender && filters.gender !== 'All Genders') {
        if (reg.gender !== filters.gender) return false;
      }
      
      if (filters.dateFrom) {
        if (new Date(reg.created_at) < new Date(filters.dateFrom)) return false;
      }
      
      if (filters.dateTo) {
        if (new Date(reg.created_at) > new Date(filters.dateTo)) return false;
      }
      
      return true;
    });
    
    if (approvedRegistrations.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV content - each student appears once per event
    // Format: E;Last Name;First Name;;Gender;Date of Birth;Venue;School;;;Event;Registration Date;Age Group
    
    // Event code mapping
    const eventCodes: { [key: string]: string } = {
      'Discus Throw': 'DT',
      'High Jump': 'HJ',
      'Javelin Throw': 'JT',
      'Shot Put': 'SP',
      'Tethalon': 'TET',
      'Triple Jump': 'TJ',
      '100m': '100',
      '200m': '200',
      '300m': '300',
      '1000m': '1000',
      '400m': '400',
      '800m': '800',
      '1500m': '1500',
      '3000m': '3000',
      '4x100 relay': '4x100',
      '4x400 relay': '4x400'
    };

    const csvRows = approvedRegistrations.map(reg => {
      const student = groupedStudents.find(s => 
        s.schoolId === reg.school_id && 
        s.firstName === reg.first_name && 
        s.lastName === reg.last_name &&
        s.dateOfBirth === reg.date_of_birth
      );
      
      // Format date of birth
      const dateOfBirth = reg.date_of_birth ? new Date(reg.date_of_birth).toLocaleDateString() : '';
      
      // Get event code, fallback to original sport name if not found
      const eventCode = eventCodes[reg.sport] || reg.sport;
      
      return [
        'E',  // E prefix for all rows
        `"${reg.last_name}"`,
        `"${reg.first_name}"`,
        '',   // Empty column
        `"${reg.gender.charAt(0).toUpperCase() + reg.gender.slice(1)}"`,
        `"${dateOfBirth}"`,
        'Venue',  // Placeholder venue text
        `"${student?.school || 'Unknown School'}"`,
        '',   // Empty column
        '',   // Empty column
        `"${eventCode}"`,
        `"${new Date(reg.created_at).toLocaleDateString()}"`,
        `"${reg.age_group}"`
      ].join(';');
    });

    // Create CSV content without headers - only data rows
    const csvContent = csvRows.join('\n');

    try {
      // Create and download CSV file
      const blob = new Blob([csvContent], { 
        type: 'text/csv;charset=utf-8;' 
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `athletics_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error creating CSV file:', error);
      alert('Error creating CSV file. Please try again.');
    }
  };

  // Function to organize data by age group and sports for printing
  const organizeDataBySports = () => {
    const sportsSections: { [sectionKey: string]: Registration[] } = {};
    
    // Get all approved registrations that match filters
    const approvedRegistrations = registrations.filter(reg => {
      if (reg.status !== 'approved') return false;
      
      // Apply filters
      // For non-coach and non-headteacher users, apply school filter if selected and not "All Schools"
      // For coaches and headteachers, school filtering is already done at data loading level
      if (!['coach', 'headteacher', 'headmaster'].includes(user?.role || '') && filters.school && filters.school !== 'All Schools') {
        // We'll need to check school name, but for now check if we have the data
        const matchingStudent = groupedStudents.find(s => s.schoolId === reg.school_id);
        if (!matchingStudent || matchingStudent.school !== filters.school) return false;
      }
      
      if (filters.region && filters.region !== 'All Regions') {
        const schoolWithRegion = schoolsWithRegions.find(s => s.id === reg.school_id);
        if (!schoolWithRegion || schoolWithRegion.region !== filters.region) return false;
      }
      
      if (filters.event && filters.event !== 'All Events') {
        if (reg.sport !== filters.event) return false;
      }
      
      if (filters.ageGroup && filters.ageGroup !== 'All Age Groups') {
        if (reg.age_group !== filters.ageGroup) return false;
      }
      
      if (filters.gender && filters.gender !== 'All Genders') {
        if (reg.gender !== filters.gender) return false;
      }
      
      if (filters.dateFrom) {
        if (new Date(reg.created_at) < new Date(filters.dateFrom)) return false;
      }
      
      if (filters.dateTo) {
        if (new Date(reg.created_at) > new Date(filters.dateTo)) return false;
      }
      
      return true;
    });
    
    // Group by age group + gender + sport combination
    approvedRegistrations.forEach(reg => {
      const genderTitle = reg.gender.charAt(0).toUpperCase() + reg.gender.slice(1);
      const sectionKey = `${reg.age_group} - ${genderTitle} - ${reg.sport}`;
      if (!sportsSections[sectionKey]) {
        sportsSections[sectionKey] = [];
      }
      sportsSections[sectionKey].push(reg);
    });
    
    // Sort sections alphabetically for consistent ordering
    const sortedSections: { [sectionKey: string]: Registration[] } = {};
    Object.keys(sportsSections).sort().forEach(key => {
      sortedSections[key] = sportsSections[key];
    });
    
    return sortedSections;
  };

  const printReport = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print the report');
      return;
    }

    // Organize data by sports
    const sportsSections = organizeDataBySports();
    const totalRegistrations = Object.values(sportsSections).flat().length;

    // Create printable HTML content with elegant light theme (ink-efficient)
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Athletics Report - ${new Date().toLocaleDateString()}</title>
          <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
          <style>
            * {
              box-sizing: border-box;
            }
            body { 
              font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; 
              margin: 0;
              padding: 30px;
              background: #fff;
              color: #1f2937;
            }
            .report-container {
              max-width: 1100px;
              margin: 0 auto;
            }
            .header { 
              text-align: center; 
              margin-bottom: 35px;
              padding-bottom: 25px;
              border-bottom: 3px solid #D4AF37;
              position: relative;
            }
            .header-icon {
              width: 60px;
              height: 60px;
              border: 2px solid #D4AF37;
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 15px;
            }
            .header-icon svg {
              width: 32px;
              height: 32px;
              stroke: #D4AF37;
            }
            .header h1 { 
              font-family: 'Bebas Neue', sans-serif;
              font-size: 38px;
              font-weight: 400;
              letter-spacing: 4px;
              color: #1a1f2e;
              margin: 0 0 8px 0;
              text-transform: uppercase;
            }
            .header p { 
              margin: 0;
              font-size: 13px;
              color: #6b7280;
              letter-spacing: 1px;
            }
            .summary-section {
              border: 2px solid #D4AF37;
              border-radius: 12px;
              padding: 20px 25px;
              margin-bottom: 30px;
            }
            .summary-header {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 15px;
              padding-bottom: 12px;
              border-bottom: 1px solid #e5e7eb;
            }
            .summary-header svg {
              width: 22px;
              height: 22px;
              stroke: #D4AF37;
            }
            .summary-header h3 {
              font-family: 'Bebas Neue', sans-serif;
              font-size: 20px;
              font-weight: 400;
              letter-spacing: 2px;
              color: #1a1f2e;
              margin: 0;
            }
            .summary-grid {
              display: flex;
              flex-wrap: wrap;
              gap: 8px 25px;
            }
            .summary-item {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 13px;
            }
            .summary-item .label {
              color: #6b7280;
              font-weight: 500;
            }
            .summary-item .value {
              color: #1f2937;
              font-weight: 600;
            }
            .sport-section {
              margin-bottom: 28px;
              page-break-inside: avoid;
            }
            .sport-header {
              background: #D4AF37;
              color: #fff;
              padding: 12px 20px;
              margin-bottom: 0;
              border-radius: 8px 8px 0 0;
              font-family: 'Bebas Neue', sans-serif;
              font-size: 16px;
              font-weight: 400;
              letter-spacing: 2px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .sport-header .count {
              background: rgba(255,255,255,0.25);
              padding: 3px 12px;
              border-radius: 15px;
              font-family: 'DM Sans', sans-serif;
              font-size: 12px;
              font-weight: 600;
            }
            table { 
              width: 100%; 
              border-collapse: collapse;
              border: 1px solid #d1d5db;
              border-top: none;
              border-radius: 0 0 8px 8px;
              overflow: hidden;
            }
            th { 
              background: #f9fafb;
              font-weight: 600;
              color: #374151;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              padding: 12px 16px;
              text-align: left;
              border-bottom: 1px solid #e5e7eb;
            }
            td { 
              padding: 11px 16px;
              text-align: left;
              font-size: 13px;
              color: #4b5563;
              border-bottom: 1px solid #f3f4f6;
            }
            tr:last-child td {
              border-bottom: none;
            }
            tr:nth-child(even) { 
              background: #fafafa;
            }
            .student-name {
              font-weight: 600;
              color: #1f2937;
            }
            .gender-badge {
              display: inline-block;
              padding: 2px 10px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 600;
              text-transform: capitalize;
            }
            .gender-male {
              background: #dbeafe;
              color: #1d4ed8;
            }
            .gender-female {
              background: #fce7f3;
              color: #be185d;
            }
            .footer {
              margin-top: 35px;
              text-align: center;
              font-size: 12px;
              color: #9ca3af;
              padding-top: 20px;
              border-top: 1px solid #e5e7eb;
            }
            .footer p {
              margin: 0;
            }
            @media print {
              body { 
                padding: 15px;
              }
              .header { page-break-after: avoid; }
              .sport-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="header">
              <div class="header-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <h1>Athletics Registration Report</h1>
              <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            </div>
            
            <div class="summary-section">
              <div class="summary-header">
                <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                <h3>Report Summary</h3>
              </div>
              <div class="summary-grid">
                <div class="summary-item">
                  <span class="label">Total Records:</span>
                  <span class="value">${totalRegistrations} approved</span>
                </div>
                <div class="summary-item">
                  <span class="label">Categories:</span>
                  <span class="value">${Object.keys(sportsSections).length} groups</span>
                </div>
                ${filters.school ? `<div class="summary-item"><span class="label">School:</span><span class="value">${filters.school}</span></div>` : ''}
                ${filters.region ? `<div class="summary-item"><span class="label">Region:</span><span class="value">${filters.region}</span></div>` : ''}
                ${filters.event ? `<div class="summary-item"><span class="label">Event:</span><span class="value">${filters.event}</span></div>` : ''}
                ${filters.ageGroup ? `<div class="summary-item"><span class="label">Age Group:</span><span class="value">${filters.ageGroup}</span></div>` : ''}
                ${filters.gender ? `<div class="summary-item"><span class="label">Gender:</span><span class="value">${filters.gender === 'male' ? 'Male' : 'Female'}</span></div>` : ''}
                ${filters.dateFrom || filters.dateTo ? `<div class="summary-item"><span class="label">Date Range:</span><span class="value">${filters.dateFrom || 'Any'} - ${filters.dateTo || 'Any'}</span></div>` : ''}
              </div>
            </div>

            ${Object.entries(sportsSections).map(([sectionName, registrations]) => `
              <div class="sport-section">
                <div class="sport-header">
                  <span>${sectionName}</span>
                  <span class="count">${registrations.length} participant${registrations.length !== 1 ? 's' : ''}</span>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Gender</th>
                      <th>School</th>
                      <th>Registration Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${registrations.map(reg => {
                      const student = groupedStudents.find(s => 
                        s.schoolId === reg.school_id && 
                        s.firstName === reg.first_name && 
                        s.lastName === reg.last_name &&
                        s.dateOfBirth === reg.date_of_birth
                      );
                      return `
                        <tr>
                          <td class="student-name">${reg.first_name} ${reg.last_name}</td>
                          <td><span class="gender-badge gender-${reg.gender}">${reg.gender}</span></td>
                          <td>${student?.school || 'Unknown School'}</td>
                          <td>${new Date(reg.created_at).toLocaleDateString()}</td>
                        </tr>
                      `;
                    }).join('')}
                  </tbody>
                </table>
              </div>
            `).join('')}

            <div class="footer">
              <p>© 2025 School Athletics Registration System</p>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <>
      <DashboardHeader />
      <div className="reports-container">
      {loading ? (
        <div className="reports-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading registration data...</p>
        </div>
      ) : (
      <div className="reports-wrapper">
        {/* Header */}
        <div className="reports-header">
          <div className="reports-header-content">
            <div>
              <h1 className="reports-title">
                Athletics Reports
              </h1>
              <p className="reports-description">
                Comprehensive analytics and reporting for athletics registrations.
              </p>
            </div>
            <div className="reports-buttons">
              <button
                onClick={downloadCSV}
                className="reports-btn reports-btn-download"
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download CSV
              </button>
              <button
                onClick={printReport}
                className="reports-btn reports-btn-print"
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Report
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Filters Button - Only visible on mobile */}
        <button
          className="mobile-filters-btn"
          onClick={() => setShowFiltersPopup(true)}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
           Open Filters ({Object.values(filters).filter(v => v).length} active)
        </button>

        {/* Filters */}
        <div className="desktop-filters reports-filters">
          <h3 className="filters-title">
            Report Filters
          </h3>
          
          <div className="filters-grid">
            {/* Only show school filter for admin users */}
            {!['coach', 'headteacher', 'headmaster'].includes(user?.role || '') && (
              <div className="filter-group" style={{ position: 'relative', zIndex: 100 }}>
                <label className="filter-label">
                  School
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={schoolSearchQuery}
                    onChange={(e) => {
                      setSchoolSearchQuery(e.target.value);
                    }}
                    placeholder={filters.school || "Search schools..."}
                    className="filter-select"
                    style={{ 
                      width: '100%',
                      cursor: 'text',
                      paddingRight: '2.5rem'
                    }}
                  />
                  {/* Clear button */}
                  {(filters.school || schoolSearchQuery) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSchoolSearchQuery('');
                        handleFilterChange('school', '');
                      }}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  )}
                  {/* Dropdown - only shows when user types */}
                  {schoolSearchQuery.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '250px',
                        overflowY: 'auto',
                        background: 'linear-gradient(135deg, #1a1f2e 0%, #0f1318 100%)',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        borderRadius: '8px',
                        marginTop: '4px',
                        zIndex: 1000,
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      {/* All Schools option */}
                      <div
                        onClick={() => {
                          handleFilterChange('school', '');
                          setSchoolSearchQuery('');
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          color: !filters.school ? '#FFD700' : 'rgba(255, 255, 255, 0.8)',
                          background: !filters.school ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          fontWeight: !filters.school ? '600' : '400',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (filters.school) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          if (filters.school) e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        All Schools
                      </div>
                      {/* Filtered schools */}
                      {schools
                        .filter(school => school !== 'All Schools')
                        .filter(school => 
                          school.toLowerCase().includes(schoolSearchQuery.toLowerCase())
                        )
                        .map(school => (
                          <div
                            key={school}
                            onClick={() => {
                              handleFilterChange('school', school);
                              setSchoolSearchQuery('');
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              cursor: 'pointer',
                              color: filters.school === school ? '#FFD700' : 'rgba(255, 255, 255, 0.8)',
                              background: filters.school === school ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                              fontWeight: filters.school === school ? '600' : '400',
                              transition: 'all 0.2s ease',
                              fontSize: '0.9rem'
                            }}
                            onMouseEnter={(e) => {
                              if (filters.school !== school) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              if (filters.school !== school) e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            {school}
                          </div>
                        ))
                      }
                      {schools.filter(school => school !== 'All Schools').filter(school => 
                        school.toLowerCase().includes(schoolSearchQuery.toLowerCase())
                      ).length === 0 && (
                        <div style={{
                          padding: '1rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          No schools found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="filter-group">
              <label className="filter-label">
                Region
              </label>
              <select
                value={filters.region}
                onChange={(e) => handleFilterChange('region', e.target.value)}
                className="filter-select"
              >
                {regions.map(region => (
                  <option key={region} value={region === 'All Regions' ? '' : region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                Event
              </label>
              <select
                value={filters.event}
                onChange={(e) => handleFilterChange('event', e.target.value)}
                className="filter-select"
              >
                {events.map(event => (
                  <option key={event} value={event === 'All Events' ? '' : event}>
                    {event}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                Age Group
              </label>
              <select
                value={filters.ageGroup}
                onChange={(e) => handleFilterChange('ageGroup', e.target.value)}
                className="filter-select"
              >
                {ageGroups.map(ageGroup => (
                  <option key={ageGroup} value={ageGroup === 'All Age Groups' ? '' : ageGroup}>
                    {ageGroup}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                Gender
              </label>
              <select
                value={filters.gender}
                onChange={(e) => handleFilterChange('gender', e.target.value)}
                className="filter-select"
              >
                {genders.map(gender => (
                  <option key={gender} value={gender === 'All Genders' ? '' : gender}>
                    {gender === 'male' ? 'Male' : gender === 'female' ? 'Female' : gender}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filters-date-row">
            <div className="filter-group">
              <label className="filter-label">
                From Date
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">
                To Date
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="filter-input"
              />
            </div>

            <button
              onClick={resetFilters}
              className="reset-filters-btn"
            >
              Reset Filters
            </button>
          </div>

          <div className="results-count">
            <span className="count-number">{filteredStudents.length}</span> registration{filteredStudents.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Student List */}
        <div className="reports-data">
          <div className="reports-data-header">
            <h3 className="reports-data-title">
              Registration Details
            </h3>
          </div>

          {filteredStudents.length === 0 ? (
            <div className="reports-empty">
              <div className="reports-empty-icon" style={{
                width: '80px',
                height: '80px',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%)',
                border: '1px solid rgba(212, 175, 55, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                fontSize: 'unset'
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 20V10"/>
                  <path d="M12 20V4"/>
                  <path d="M6 20v-6"/>
                  <rect x="2" y="3" width="20" height="18" rx="2" stroke="#D4AF37" strokeWidth="1.5" fill="none"/>
                </svg>
              </div>
              <h3 className="reports-empty-title">
                No Data Found
              </h3>
              <p className="reports-empty-text">No registrations match your current filters. Try adjusting the filters above.</p>
            </div>
          ) : (
            (() => {
              // Pagination logic
              const totalItems = filteredStudents.length;
              const totalPages = Math.ceil(totalItems / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const currentPageItems = filteredStudents.slice(startIndex, endIndex);

              return (
                <>
                  <div className="reports-data-content">
                    <div className="table-grid">
                      <div>Student Name</div>
                      <div>Gender</div>
                      <div>Age Group</div>
                      <div>School</div>
                      <div>Events</div>
                      <div>Registration Date</div>
                    </div>

                    {currentPageItems.map((student, index) => (
                      <>
                        {/* Desktop Table Row */}
                        <div 
                          key={student.id}
                          className="table-row"
                        >
                          <div className="table-cell table-cell-name">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="table-cell table-cell-secondary">
                            {student.gender}
                          </div>
                          <div className="table-cell table-cell-secondary">
                            {student.ageGroup}
                          </div>
                          <div className="table-cell table-cell-secondary">
                            {student.school}
                          </div>
                          <div className="table-cell table-cell-events">
                            {student.events.join(', ')}
                          </div>
                          <div className="table-cell table-cell-secondary">
                            {new Date(student.registrationDate).toLocaleDateString()}
                          </div>
                        </div>
                        
                        {/* Mobile Card */}
                        <div key={`mobile-${student.id}`} className="mobile-card">
                          <div className="mobile-card-header">
                            <span className="mobile-card-name">{student.firstName} {student.lastName}</span>
                            <span className="mobile-card-events">{student.events.join(', ')}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Gender</span>
                            <span className="mobile-card-value" style={{ textTransform: 'capitalize' }}>{student.gender}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Age Group</span>
                            <span className="mobile-card-value">{student.ageGroup}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">School</span>
                            <span className="mobile-card-value">{student.school}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Registered</span>
                            <span className="mobile-card-value">{new Date(student.registrationDate).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <div className="pagination-info">
                        Showing <span className="highlight">{startIndex + 1}</span> to <span className="highlight">{Math.min(endIndex, totalItems)}</span> of <span className="highlight">{totalItems}</span> entries
                      </div>
                      
                      <div className="pagination-controls">
                        <button
                          className="pagination-btn"
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                          <button
                            key={pageNum}
                            className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        ))}
                        
                        <button
                          className="pagination-btn"
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </div>
      </div>
      )}

      {/* Filters Popup Modal */}
      {showFiltersPopup && (
        <div
          className="filters-popup-overlay"
          onClick={() => setShowFiltersPopup(false)}
        >
          <div
            className="filters-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="filters-popup-header">
              <h3 className="filters-popup-title">
                Report Filters
              </h3>
              <button
                className="filters-popup-close"
                onClick={() => setShowFiltersPopup(false)}
              >
                ×
              </button>
            </div>
            
            <div className="filters-popup-body">
              <div className="filters-popup-grid">
                {/* Only show school filter for admin users */}
                {!['coach', 'headteacher', 'headmaster'].includes(user?.role || '') && (
                  <div className="filter-group">
                    <label className="filter-label">School</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        background: '#1a1f2e',
                        border: '1px solid rgba(255, 215, 0, 0.2)',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <input
                          type="text"
                          value={mobileSchoolSearchQuery}
                          onChange={(e) => {
                            setMobileSchoolSearchQuery(e.target.value);
                          }}
                          placeholder={filters.school || "Search schools..."}
                          style={{
                            flex: 1,
                            padding: '10px 12px',
                            background: 'transparent',
                            border: 'none',
                            color: '#f9fafb',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        />
                        {filters.school && (
                          <button
                            onClick={() => {
                              handleFilterChange('school', '');
                              setMobileSchoolSearchQuery('');
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#9ca3af',
                              padding: '0 12px',
                              cursor: 'pointer',
                              fontSize: '18px'
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      
                      {mobileSchoolSearchQuery.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: '4px',
                            background: '#1a1f2e',
                            border: '1px solid rgba(255, 215, 0, 0.3)',
                            borderRadius: '8px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 100,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                          }}>
                            <div
                              onClick={() => {
                                handleFilterChange('school', '');
                                setMobileSchoolSearchQuery('');
                              }}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                color: !filters.school ? '#FFD700' : '#9ca3af',
                                background: !filters.school ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                                borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                                fontWeight: !filters.school ? '600' : 'normal',
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (filters.school) e.currentTarget.style.background = 'rgba(255, 215, 0, 0.05)';
                              }}
                              onMouseLeave={(e) => {
                                if (filters.school) e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              All Schools
                            </div>
                            {schools
                              .filter(school => school !== 'All Schools')
                              .filter(school => 
                                school.toLowerCase().includes(mobileSchoolSearchQuery.toLowerCase())
                              )
                              .map(school => (
                                <div
                                  key={school}
                                  onClick={() => {
                                    handleFilterChange('school', school);
                                    setMobileSchoolSearchQuery('');
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    color: filters.school === school ? '#FFD700' : '#d1d5db',
                                    background: filters.school === school ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                                    fontWeight: filters.school === school ? '600' : 'normal',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (filters.school !== school) e.currentTarget.style.background = 'rgba(255, 215, 0, 0.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (filters.school !== school) e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  {school}
                                </div>
                              ))}
                          </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="filter-group">
                  <label className="filter-label">Region</label>
                  <select
                    value={filters.region}
                    onChange={(e) => handleFilterChange('region', e.target.value)}
                    className="filter-select"
                  >
                    {regions.map(region => (
                      <option key={region} value={region === 'All Regions' ? '' : region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Event</label>
                  <select
                    value={filters.event}
                    onChange={(e) => handleFilterChange('event', e.target.value)}
                    className="filter-select"
                  >
                    {events.map(event => (
                      <option key={event} value={event === 'All Events' ? '' : event}>
                        {event}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Age Group</label>
                  <select
                    value={filters.ageGroup}
                    onChange={(e) => handleFilterChange('ageGroup', e.target.value)}
                    className="filter-select"
                  >
                    {ageGroups.map(ageGroup => (
                      <option key={ageGroup} value={ageGroup === 'All Age Groups' ? '' : ageGroup}>
                        {ageGroup}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Gender</label>
                  <select
                    value={filters.gender}
                    onChange={(e) => handleFilterChange('gender', e.target.value)}
                    className="filter-select"
                  >
                    {genders.map(gender => (
                      <option key={gender} value={gender === 'All Genders' ? '' : gender}>
                        {gender === 'male' ? 'Male' : gender === 'female' ? 'Female' : gender}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">From Date</label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className="filter-input"
                  />
                </div>
                
                <div className="filter-group">
                  <label className="filter-label">To Date</label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="filters-popup-actions">
                <button
                  onClick={resetFilters}
                  className="filters-popup-reset"
                >
                  Reset Filters
                </button>
                
                <button
                  onClick={() => setShowFiltersPopup(false)}
                  className="filters-popup-apply"
                >
                  Apply Filters
                </button>
              </div>

              <div className="filters-popup-count">
                {filteredStudents.length} registration{filteredStudents.length !== 1 ? 's' : ''} found
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Reports;
